import { useEffect, useState } from 'react';
import {
  FireSignalReadError,
  type createFireSignalAdapter,
} from '../../adapters/fire-signals/http';
import type { WorkspaceResearchData } from '../../consumer/command-workspace-contract';
import type { Candidate, ResultPage } from '../../consumer/fire-signals/contract';
import { GEO_UI_KEYS } from '../../consumer/geospatial/config';
import type { MonitoringReadState } from '../../consumer/polling';
import { initialReadState } from '../../consumer/polling';

export interface WorkspaceReadScheduler {
  run<T>(read: (signal: AbortSignal) => Promise<T>, signal: AbortSignal): Promise<T>;
}
/** Per-page FIFO admission. A network group still owns its existing pair of GETs.
 * Two groups bound stable monitoring to at most three HTTP requests; network plus
 * historical transition work can use at most the existing four-worker budget.
 * This does not identify or remedy a backend timeout cause.
 */
export function createWorkspaceReadScheduler(limit: 1 | 2 = 2): WorkspaceReadScheduler {
  const queue: { start: () => void }[] = [];
  let running = 0;
  const drain = () => {
    while (running < limit && queue.length) {
      const job = queue.shift()!;
      running++;
      job.start();
    }
  };
  return {
    run<T>(read: (signal: AbortSignal) => Promise<T>, signal: AbortSignal): Promise<T> {
      if (signal.aborted) return Promise.reject(new DOMException('Aborted', 'AbortError'));
      return new Promise<T>((resolve, reject) => {
        let started = false;
        let settled = false;
        const abort = () => {
          if (settled) return;
          settled = true;
          reject(new DOMException('Aborted', 'AbortError'));
          signal.removeEventListener('abort', abort);
          if (!started) {
            const index = queue.indexOf(job);
            if (index >= 0) queue.splice(index, 1);
            drain();
          }
          // An active ignored-abort read retains its slot until it actually settles.
        };
        const release = () => {
          signal.removeEventListener('abort', abort);
          running--;
          drain();
        };
        const job = {
          start: () => {
            started = true;
            void Promise.resolve()
              .then(() => {
                if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
                return read(signal);
              })
              .then(
                (data) => {
                  if (!settled) {
                    settled = true;
                    resolve(data);
                  }
                  release();
                },
                (error: unknown) => {
                  if (!settled) {
                    settled = true;
                    reject(error);
                  }
                  release();
                },
              );
          },
        };
        signal.addEventListener('abort', abort, { once: true });
        queue.push(job);
        drain();
      });
    },
  };
}
export function readFailure(error: unknown): string {
  if (error instanceof Error) {
    if ('status' in error && 'code' in error)
      return `${String(error.status || 'NETWORK')} · ${String(error.code)}`;
    return error.message;
  }
  return 'UNKNOWN · 读取失败';
}
/** UI controls never become API predicates. All other predicates remain for API validation. */
export function researchQuery(search: string): string {
  const query = new URLSearchParams(search);
  for (const key of [
    'mode',
    'filter',
    'selected',
    'areaId',
    'view',
    'dataSource',
    'demoArea',
    ...GEO_UI_KEYS,
  ])
    query.delete(key);
  return query.toString();
}
export async function readResearchScope(
  adapter: ReturnType<typeof createFireSignalAdapter>,
  search: string,
  signal: AbortSignal,
): Promise<WorkspaceResearchData> {
  const scope = researchQuery(search);
  const [summary, sources] = await Promise.all([
    adapter.summary(scope, signal),
    adapter.sources(signal),
  ]);
  const items: Candidate[] = [];
  let total = 0;
  let first: ResultPage<Candidate> | null = null;
  do {
    const query = new URLSearchParams(scope);
    query.append('limit', '100'); // backend permits 1–200; no prototype count assumption
    query.append('offset', String(items.length));
    const page = await adapter.candidates(query.toString(), signal);
    if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
    if (!first) {
      first = page;
      total = page.total;
    }
    if (
      page.total !== total ||
      page.offset !== items.length ||
      page.limit !== 100 ||
      (page.items.length === 0 && items.length < total) ||
      items.length + page.items.length > total
    )
      throw new FireSignalReadError(200, 'INCONSISTENT_PAGE');
    items.push(...page.items);
  } while (items.length < total);
  if (new Set(items.map((c) => c.candidateId)).size !== items.length)
    throw new FireSignalReadError(200, 'DUPLICATE_CANDIDATE');
  return {
    summary,
    sources,
    candidates: { ...first!, items, offset: 0, total, limit: items.length },
  };
}
/** Independent reads preserve typed failures, abort on scope changes, and ignore late promises. */
export function useWorkspaceRead<T>(
  read: ((signal: AbortSignal) => Promise<T>) | null,
  refresh: number,
  interval = 0,
  scheduler?: WorkspaceReadScheduler,
): MonitoringReadState<T> {
  const [result, setResult] = useState<{ read: typeof read; state: MonitoringReadState<T> }>({
    read,
    state: initialReadState<T>(),
  });
  useEffect(() => {
    let active = true;
    let inFlight = false;
    let controller: AbortController | null = null;
    let state = initialReadState<T>();
    setResult({ read, state });
    const poll = async () => {
      if (!active || inFlight || !read) return;
      inFlight = true;
      controller = new AbortController();
      try {
        const data = scheduler
          ? await scheduler.run(read, controller.signal)
          : await read(controller.signal);
        if (active) {
          state = { status: 'READY', data, lastReadAt: Date.now(), error: null };
          setResult({ read, state });
        }
      } catch (error) {
        if (active) {
          state = { ...state, status: 'ERROR', error: readFailure(error) };
          setResult({ read, state });
        }
      } finally {
        inFlight = false;
      }
    };
    void poll();
    const timer = read && interval ? setInterval(() => void poll(), interval) : null;
    return () => {
      active = false;
      if (timer !== null) clearInterval(timer);
      controller?.abort();
    };
  }, [read, refresh, interval, scheduler]);
  return result.read === read ? result.state : initialReadState<T>();
}
